import {
  requiredEquipmentNumberValidator,
  requiredStoppedMainEquipmentNumberValidator,
  stoppageCodeValidator,
  stoppageReasonValidator,
  validFromValidatorWithOverlapCheck,
  validToValidatorWithOverlapCheck,
  mapPartialStoppage,
  mapStoppageDto
} from '@domain/shifthandover'
import {
  DisabledFields,
  EquipmentIdType,
  StoppageDto,
  HiddenFields,
  DowntimeStoppageCodes,
  Stoppage
} from '@hconnect/common/types'
import {
  shouldNotBeEmpty,
  shouldNotBeEmptyString,
  ValidatorConfig
} from '@hconnect/common/validators'
import {TFunction} from 'i18next'
import React, {useCallback} from 'react'

import {StoppageFormFields} from './StoppageFormFields'

type PossibleErrors = keyof StoppageDto | 'stoppageCode' | 'stoppageReason' | 'equipmentNumber'

type StoppageFormProps = {
  item: Partial<StoppageDto>
  updateMultiPart: (delta: Partial<StoppageDto>) => void
  validationError: Map<PossibleErrors, string>
  plantId: string
  children?: React.ReactNode
  disabledFields?: DisabledFields
  hiddenFields?: HiddenFields
  autocompleteProcessStage?: boolean
  equipmentIdType?: EquipmentIdType
  timezone: string
  stoppageCodes: DowntimeStoppageCodes
  isFunctionalLocationRequired?: boolean
  isDescriptionOptional?: boolean
}

export const getStoppageValidatorConfig = (
  t: TFunction,
  existingEvent?: Stoppage,
  skipOverlapValidation?: boolean
): ValidatorConfig<StoppageDto> =>
  new Map([
    ['title', shouldNotBeEmptyString<StoppageDto>()],
    ['equipmentNumber', requiredEquipmentNumberValidator],
    ['stoppedMainEquipmentNumbers', requiredStoppedMainEquipmentNumberValidator],
    ['eventType', shouldNotBeEmpty<StoppageDto>()],
    ['stoppageCode', stoppageCodeValidator],
    ['stoppageReason', stoppageReasonValidator],
    [
      'stoppageStart',
      validFromValidatorWithOverlapCheck(t, existingEvent, undefined, skipOverlapValidation)
    ],
    [
      'stoppageEnd',
      validToValidatorWithOverlapCheck(t, existingEvent, undefined, skipOverlapValidation)
    ],
    ['description', shouldNotBeEmptyString<StoppageDto>()]
    // HCP-42934 uncomment when management decides
    // ['costCurrency', shouldNotBeEmptyString<Stoppage>()],
    // ['cost', costValidator]
  ])

export const StoppageForm: React.FC<StoppageFormProps> = ({
  children,
  item,
  updateMultiPart,
  validationError,
  autocompleteProcessStage,
  hiddenFields,
  disabledFields,
  equipmentIdType,
  timezone,
  plantId,
  stoppageCodes,
  isFunctionalLocationRequired,
  isDescriptionOptional
}) => {
  const onChange = useCallback(
    (delta: Partial<Stoppage>) => updateMultiPart(mapStoppageDto(delta)),
    [updateMultiPart]
  )

  return (
    <StoppageFormFields
      item={mapPartialStoppage(item, timezone)}
      updateMultiPart={onChange}
      validationError={validationError}
      timezone={timezone}
      plantId={plantId}
      hiddenFields={hiddenFields}
      stoppageCodes={stoppageCodes}
      disabledFields={disabledFields}
      autocompleteProcessStage={autocompleteProcessStage}
      equipmentIdType={equipmentIdType}
      isFunctionalLocationRequired={isFunctionalLocationRequired}
      isDescriptionOptional={isDescriptionOptional}
    >
      {children}
    </StoppageFormFields>
  )
}
